import React, { useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { RootState } from '../../../reducers';
import { askQuestion } from '../../selectedVideo/selectedVideoSlice';
import { useLimits } from '../../plan/hooks/limits';

const ChatSuggestions: React.FC = () => {
  const {
    file: { _id: fileId },
    chat: { suggestions = [], conversation = [] },
  } = useSelector((state: RootState) => state.selectedVideo);
  const dispatch = useDispatch();
  const guardLimits = useLimits();
  const showLimitReached = guardLimits('askAI') <= 0;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleSuggestionClick = useCallback(
    (prompt: string, event: React.MouseEvent) => {
      event.stopPropagation();

      if (showLimitReached) {
        guardLimits('askAI', () => {});
        return;
      }

      dispatch(askQuestion({ fileId, question: prompt }));
      // Auto-scroll is now handled by useAutoScroll hook
    },
    [showLimitReached, guardLimits, fileId, dispatch],
  );

  if (!suggestions.length || conversation.length > 0) return null;

  return (
    <div className="pb-2 w-full overflow-hidden">
      <div
        ref={scrollContainerRef}
        className="flex gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent pb-2 px-3"
        style={{ scrollbarWidth: 'thin' }}
      >
        {suggestions.map((suggestion, index) => (
          <Button
            key={`suggestion-${index}`}
            variant="outline"
            size="sm"
            className={cn(
              'flex-shrink-0 h-auto py-1 px-2 text-left whitespace-normal',
              'border border-border/50 bg-background/50 hover:bg-accent/50',
              'transition-all duration-200 ease-out',
              'max-w-[200px] min-w-fit',
              index === 0 && 'ml-0',
              index === suggestions.length - 1 && 'mr-0',
            )}
            onClick={(e) => handleSuggestionClick(suggestion.prompt, e)}
          >
            <span className="text-sm font-medium line-clamp-1 truncate">
              {suggestion.title}
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
};

export default ChatSuggestions;
